import {
  CAccordion,
  CAccordionBody,
  CAccordionHeader,
  CAccordionItem,
  CCloseButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CRow,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import {
  EnvelopeIcon,
  PhoneIcon,
  UserCircleIcon,
} from "@heroicons/react/24/solid";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { HeaderSort } from "../../../components/ui/TableHeaderSort";
import {
  BookingStatusDisplayValue,
  BookingTypes,
} from "../../../helpers/enums";
import { formatDateString } from "../../../helpers/general";
import apiService from "../../../service/apiService";
import { useCookie } from "../../../hooks/useCookie";
import { general } from "../../../locales/general";
import { useToast } from "../../../hooks/useToast";

interface TableBodyProps {
  booking: Booking;
  showDetails: (booking: Booking) => void;
  mode: "actions" | "list";
}

function getBookingStatusDisplayValue(booking: Booking) {
  if (booking.status?.label?.toLowerCase() === "confirmed" && booking.isUsed) {
    return BookingStatusDisplayValue.PASSED;
  } else if (booking.status?.label?.toLowerCase() === "canceled") {
    return BookingStatusDisplayValue.CANCELED;
  } else if (booking.status?.label?.toLowerCase() === "confirmed") {
    return BookingStatusDisplayValue.CONFIRMED;
  } else if (booking.status?.label?.toLowerCase() === "refused") {
    return BookingStatusDisplayValue.REFUSED;
  } else if (booking.status?.label?.toLowerCase() === "cash_pre_payment") {
    return BookingStatusDisplayValue.CASH_PRE_PAYMENT;
  }
  return BookingStatusDisplayValue.WAITING;
}

function TableBodyRowContent({ booking, showDetails, mode }: TableBodyProps) {
  const hotelCommission = booking?.placeCommission ?? 0;
  const bookingCommission = (
    (booking.priceToPay * hotelCommission) /
    100
  ).toFixed(2);

  return (
    <CTableRow
      style={{
        width: "100%",
        position: "relative",
      }}
    >
      <CTableDataCell className="px-3">
        <p className="fw-bold m-0 text-capitalize text-sm">
          {booking?.special?.title}
        </p>
      </CTableDataCell>

      <CTableDataCell className="p-3 text-capitalize text-sm">
        {formatDateString(booking?.date!)} - {booking?.hour}
      </CTableDataCell>
      {mode === "actions" ? (
        <CTableDataCell className="p-3 text-sm">
          {booking?.bookedForUser?.fullName ??
            booking?.bookedForUser?.email ??
            booking?.bookedForUser?.phoneNumber}
        </CTableDataCell>
      ) : null}
      <CTableDataCell className="p-3 text-sm">
        {booking?.reference}
      </CTableDataCell>
      <CTableDataCell className="p-3 text-sm">
        MAD {booking?.priceToPay}
      </CTableDataCell>
      <CTableDataCell className="p-3 text-sm">
        {booking?.isForAnotherUser === true
          ? `(${booking.placeCommission ?? 0}%)`
          : ""}{" "}
        MAD {bookingCommission}{" "}
      </CTableDataCell>
      <CTableDataCell className="p-3 text-sm">
        {booking?.bookingCommissionPaid?.isMultiPlaceAdminPaid ? "✅" : "❌"}
      </CTableDataCell>
      <CTableDataCell className="p-3 text-sm">
        <div className="p-3 text-capitalize text-sm d-flex align-items-center gap-3">
          <button
            onClick={() => showDetails(booking)}
            className="btn rounded-pill border py-1 px-3 text-sm"
          >
            Vue
          </button>
        </div>
      </CTableDataCell>
    </CTableRow>
  );
}

interface TableProps {
  bookings: Booking[];
  mode?: "actions" | "list";
}

export default function PlaceManagerBookingsTable({
  bookings,
  mode = "actions",
}: TableProps) {
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [sort, setSort] = useState({
    field: "createdAt",
    order: "desc",
  });
  const [searchParams] = useSearchParams();
  const { token } = useCookie("vToken");
  const { toastError } = useToast();

  const resetSort = () => setSort({ field: "createdAt", order: "desc" });

  useEffect(() => {
    if (searchParams.get("sort")) {
      const [field, order] = searchParams.get("sort")!.split(":");
      setSort({ field, order });
    } else {
      resetSort();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadBookingPdf = async () => {
    try {
      setIsDownloadingPdf(true);
      const response = await fetch(
        `${apiService.baseUrl}booking/${selectedBooking?.id}/pdf`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to download the PDF");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `booking_${selectedBooking?.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      toastError(general.fr.message.operationFailed);
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  return (
    <div className="mt-3">
      <CTable responsive>
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell scope="col" className="text-sm">
              <div className="d-flex align-items-center justify-content-between">
                Activité <div style={{ height: "40px", width: "20px" }}></div>
              </div>
            </CTableHeaderCell>

            <CTableHeaderCell scope="col" className="text-sm">
              <div className="d-flex align-items-center justify-content-between">
                Date/Heure de réservation
                {mode === "actions" ? (
                  <HeaderSort
                    field="date"
                    sort={sort.order}
                    sortedField={sort}
                    setSort={(field, order) => setSort({ field, order })}
                    clearSort={() => resetSort()}
                    defaultField="createdAt"
                  />
                ) : (
                  <div style={{ height: "40px", width: "20px" }}></div>
                )}
              </div>
            </CTableHeaderCell>
            {mode === "actions" ? (
              <CTableHeaderCell scope="col" className="text-sm">
                <div className="d-flex align-items-center justify-content-between">
                  Utilisateur{" "}
                  <div style={{ height: "40px", width: "20px" }}></div>
                </div>
              </CTableHeaderCell>
            ) : null}
            <CTableHeaderCell scope="col" className="text-sm">
              <div className="d-flex align-items-center justify-content-between">
                Référence
                {mode === "actions" ? (
                  <HeaderSort
                    field="reference"
                    sort={sort.order}
                    sortedField={sort}
                    setSort={(field, order) => setSort({ field, order })}
                    clearSort={() => resetSort()}
                    defaultField="createdAt"
                  />
                ) : (
                  <div style={{ height: "40px", width: "20px" }}></div>
                )}
              </div>
            </CTableHeaderCell>

            <CTableHeaderCell scope="col" className="text-sm">
              <div className="d-flex align-items-center justify-content-between">
                Prix total
              </div>
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" className="text-sm">
              <div className="d-flex align-items-center justify-content-between">
                Commission hôtelière
              </div>
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" className="text-sm">
              <div className="d-flex align-items-center justify-content-between">
                Commission payée
              </div>
            </CTableHeaderCell>

            <CTableHeaderCell
              scope="col"
              className="text-sm"
            ></CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {bookings?.map((booking) => (
            <TableBodyRowContent
              key={booking.id}
              booking={booking}
              showDetails={(booking: Booking) => setSelectedBooking(booking)}
              mode={mode}
            />
          ))}
        </CTableBody>
      </CTable>
      <COffcanvas
        backdrop={true}
        placement="start"
        visible={Object.keys(selectedBooking ?? {}).length > 0}
        onHide={() => setSelectedBooking(null)}
      >
        <div className="border-bottom pb-1 px-3">
          <COffcanvasHeader className="mb-0">
            <COffcanvasTitle>Détails des réservations</COffcanvasTitle>
            <CCloseButton
              className="text-reset"
              onClick={() => setSelectedBooking(null)}
            />
          </COffcanvasHeader>
          <div className="d-flex align-items-center justify-content-between">
            <div className="mb-1">
              <UserCircleIcon width={20} />{" "}
              <span className="text-sm">
                {selectedBooking?.bookedForUser?.fullName}
              </span>
            </div>
            <div className="mb-1">
              <PhoneIcon width={15} />{" "}
              <span className="text-sm">
                {selectedBooking?.bookedForUser?.phoneNumber}
              </span>
            </div>
          </div>
          <div className="mb-1">
            <EnvelopeIcon width={15} />{" "}
            <span className="text-sm">
              {selectedBooking?.bookedForUser?.email}
            </span>
          </div>
        </div>
        <COffcanvasBody>
          <CRow className="align-items-center justify-content-between">
            <CCol sm="auto">
              {selectedBooking?.place?.name ?? selectedBooking?.event?.name}
            </CCol>
            <CCol sm="auto">
              <button className="btn btn-danger rounded-full py-0 px-2 text-white">
                {selectedBooking &&
                  getBookingStatusDisplayValue(selectedBooking)}
              </button>
            </CCol>
          </CRow>

          <CRow className="align-items-center justify-content-between mt-3">
            <CCol sm="auto" title="Reference">
              <p className="text-sm mb-0">Reference</p>
              {selectedBooking?.reference}
            </CCol>
          </CRow>

          {selectedBooking?.type === BookingTypes.PLACE_BOOKING ||
          (selectedBooking?.type === BookingTypes.SPECIAL_OFFER_BOOKING &&
            selectedBooking?.variantsBookings!?.length === 0) ? (
            <CRow className="bg-primary-shade p-2 rounded-sm align-items-center justify-content-between mt-3">
              <CCol sm="auto" title="Reference">
                Nombre de personnes: {selectedBooking?.personsNumber}
              </CCol>
            </CRow>
          ) : null}

          <CRow className="align-items-center justify-content-between mt-3">
            <CCol sm="auto" title="Reference">
              <span className="font-bold">Méthode de paiement: </span>
              {selectedBooking?.wafacashCode !== null
                ? "Wafacash"
                : selectedBooking?.paymentMethod === "credit_card" ||
                    selectedBooking?.paymentMethod === "stripe"
                  ? "Carte bancaire"
                  : selectedBooking?.isGuestList
                    ? "Invité guestlist"
                    : "Sur place"}
            </CCol>
          </CRow>

          {selectedBooking?.packsBookings!?.length > 0 && (
            <div className="mt-4">
              <p className="mb-2 border-bottom fw-bold">Pack réservé</p>
              <CAccordion flush>
                {selectedBooking?.packsBookings.map((pack) => (
                  <CAccordionItem key={pack.id}>
                    <CAccordionHeader className="align-items-center justify-content-between w-100">
                      <div className="d-flex align-items-center gap-2 w-75">
                        <img
                          src={pack.pack.image}
                          alt=""
                          width={40}
                          height={40}
                          className="rounded-sm"
                        />

                        <p className="mb-0">
                          {pack.numberOfReservation} x {pack.pack.title}{" "}
                        </p>
                      </div>
                      {selectedBooking.priceToPayCurrency === "euro" ? (
                        <p className="mb-0">
                          {/* @ts-ignore */}
                          {pack.pack.priceInEuro * pack.numberOfReservation}€
                        </p>
                      ) : (
                        <p className="mb-0">
                          {/* @ts-ignore */}
                          {pack.pack.price * pack.numberOfReservation}
                          DH
                        </p>
                      )}
                    </CAccordionHeader>
                    <CAccordionBody>
                      <ul>
                        {pack.pack.items.map((packItem) => (
                          <li key={packItem.id} className="mb-1">
                            {packItem.title}
                          </li>
                        ))}
                      </ul>
                    </CAccordionBody>
                  </CAccordionItem>
                ))}
              </CAccordion>
            </div>
          )}

          {selectedBooking?.variantsBookings!?.length > 0 && (
            <div className="mt-4 bg-primary-shade p-2 rounded-sm">
              <p className="mb-2 border-bottom fw-bold">Variant reservé</p>
              {selectedBooking?.variantsBookings.map((item) => (
                <CRow
                  key={item.id}
                  className="mb-2 align-items-center justify-content-between"
                >
                  <CCol sm="auto">
                    {item.numberOfReservation} x {item.variant.description}
                  </CCol>
                  {selectedBooking.priceToPayCurrency === "euro" ? (
                    <CCol sm="auto">
                      {item.numberOfReservation *
                        parseFloat(item.variant.priceInEuro)}{" "}
                      €
                    </CCol>
                  ) : (
                    <CCol sm="auto">
                      {item.numberOfReservation *
                        parseFloat(item.variant.price)}{" "}
                      DH
                    </CCol>
                  )}
                </CRow>
              ))}
            </div>
          )}

          {selectedBooking?.servicesBookings!?.length > 0 && (
            <div className="mt-4 bg-primary-shade p-2 rounded-sm">
              <p className="mb-2 border-bottom fw-bold">Service réservé</p>
              {selectedBooking?.servicesBookings.map((item) => (
                <CRow
                  key={item.id}
                  className="mb-2 align-items-center justify-content-between"
                >
                  <CCol sm="auto">
                    {item.personsNumber} x {item.service.label}
                  </CCol>
                  {selectedBooking.priceToPayCurrency === "euro" ? (
                    <CCol sm="auto">
                      {item.personsNumber * item.service.startingPriceEuro} €
                    </CCol>
                  ) : (
                    <CCol sm="auto">
                      {item.personsNumber * item.service.startingPrice} DH
                    </CCol>
                  )}
                </CRow>
              ))}
              {selectedBooking?.appliedPromoCodes &&
                selectedBooking?.appliedPromoCodes?.length > 0 && (
                  <>
                    <CRow className=" mt-3 pt-2 mb-2 align-items-center justify-content-between border-top">
                      <CCol sm="auto">
                        <strong style={{ fontSize: "14px" }}>
                          Sous-Total:
                        </strong>
                      </CCol>
                      {selectedBooking.priceToPayCurrency === "euro" ? (
                        <CCol sm="auto">
                          {selectedBooking?.servicesBookings?.reduce(
                            (total, service) => {
                              return (
                                total +
                                service.service.startingPriceEuro *
                                  service.personsNumber
                              );
                            },
                            0,
                          )}{" "}
                          €
                        </CCol>
                      ) : (
                        <CCol sm="auto">
                          {selectedBooking?.servicesBookings?.reduce(
                            (total, service) => {
                              return (
                                total +
                                service.service.startingPrice *
                                  service.personsNumber
                              );
                            },
                            0,
                          )}{" "}
                          DH
                        </CCol>
                      )}
                    </CRow>
                    {selectedBooking && (
                      <CRow className="mb-2 align-items-center justify-content-between">
                        <CCol sm="auto">
                          <strong style={{ fontSize: "14px" }}>
                            {" "}
                            Remise (
                            {selectedBooking?.appliedPromoCodes[0].discount}
                            %):
                          </strong>
                        </CCol>
                        {selectedBooking.priceToPayCurrency === "euro" ? (
                          <CCol sm="auto">
                            -
                            {selectedBooking?.servicesBookings.reduce(
                              (total, service) =>
                                total +
                                service.service.startingPriceEuro *
                                  service.personsNumber,
                              0,
                            ) - selectedBooking?.priceToPay}{" "}
                            €
                          </CCol>
                        ) : (
                          <CCol sm="auto">
                            -
                            {selectedBooking?.servicesBookings.reduce(
                              (total, service) =>
                                total +
                                service.service.startingPrice *
                                  service.personsNumber,
                              0,
                            ) - selectedBooking?.priceToPay}{" "}
                            DH
                          </CCol>
                        )}
                      </CRow>
                    )}
                  </>
                )}
              <CRow className="mb-2 align-items-center justify-content-between">
                <CCol sm="auto">
                  {" "}
                  <strong style={{ fontSize: "14px" }}>Total:</strong>{" "}
                </CCol>
                <CCol sm="auto">
                  <strong>
                    {selectedBooking?.priceToPay ??
                      selectedBooking?.servicesBookings?.reduce(
                        (total, service) => {
                          return (
                            total +
                            service.service.startingPrice *
                              service.personsNumber
                          );
                        },
                        0,
                      )}{" "}
                    {selectedBooking?.priceToPayCurrency === "euro"
                      ? "€"
                      : "DH"}
                  </strong>{" "}
                </CCol>
              </CRow>
            </div>
          )}

          <div className="mt-2">
            <div className="d-flex">
              <div className="me-2 font-semibold">Prix total:</div>
              <div>{selectedBooking?.priceToPay} DH</div>
            </div>
            <div className="d-flex">
              <div className="me-2 font-semibold">
                Commission hôtelière ({selectedBooking?.placeCommission ?? 0}%):
              </div>
              <div>
                {(
                  ((selectedBooking?.priceToPay ?? 0) *
                    (selectedBooking?.placeCommission ?? 0)) /
                  100
                ).toFixed(2)}{" "}
                DH
              </div>
            </div>
            <div className="d-flex">
              <div className="me-2 font-semibold">Commission payée:</div>
              <div>
                {selectedBooking?.bookingCommissionPaid?.isMultiPlaceAdminPaid
                  ? "✅"
                  : "❌"}
              </div>
            </div>
          </div>

          {selectedBooking?.createdAt && (
            <div className="border-top p-1 pt-2 mt-3">
              <p className="mb-0">
                Date de création :{" "}
                {formatDateString(selectedBooking?.createdAt)}
              </p>
              <p className="mb-0">
                Heure de création :{" "}
                {format(new Date(selectedBooking?.createdAt), "HH:mm")}
              </p>
            </div>
          )}

          {selectedBooking?.date && (
            <div className="border-top p-1 pt-2 mt-3">
              <p className="mb-0">
                Date de réservation : {formatDateString(selectedBooking?.date)}
              </p>
              <p className="mb-0">
                Heure de réservation : {selectedBooking?.hour}
              </p>
            </div>
          )}

          {selectedBooking?.scanedBy && (
            <div className="border-top p-1 mt-3">
              <p className="mb-0">
                Scanné par: {selectedBooking?.scanedBy?.fullName}{" "}
              </p>
            </div>
          )}
          {selectedBooking?.scanedAt && (
            <div className="border-bottom p-1">
              <p className="mb-0">
                Scanné à:{" "}
                {format(
                  new Date(selectedBooking?.scanedAt),
                  "dd/MM/yyyy - HH:mm",
                )}
              </p>
            </div>
          )}

          {/* download booking qr pdf button */}
          {isDownloadingPdf ? (
            <div
              className="w-full mt-3 d-flex justify-content-center align-items-center"
              style={{
                backgroundColor: "#ff7722b0",
                borderRadius: "5px",
                height: "45px",
                aspectRatio: "1/1",
                color: "white",
                border: "none",
                outline: "none",
                padding: "0.5rem 1rem",
              }}
            >
              <CSpinner size="sm" color="#ffffff" />
            </div>
          ) : (
            <button
              className="btn btn-lg w-100 mt-3 bg-gradient-primary text-white"
              style={{
                fontSize: "18px",
              }}
              onClick={downloadBookingPdf}
            >
              Télécharger le PDF
            </button>
          )}
        </COffcanvasBody>
      </COffcanvas>
    </div>
  );
}
